/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from 'react';
import { normalizeVoiceCommand } from './voiceCommands';
import type {
  WakeWordStatus,
  UseWakeWordOptions,
  UseWakeWordReturn,
  NormalizedCommand,
  SpeechRecognition,
  SpeechRecognitionEvent,
  SpeechRecognitionErrorEvent,
} from './types';

// Re-export types for backward compatibility 
export type { WakeWordStatus, UseWakeWordOptions, UseWakeWordReturn } from './types';

// ============================================================================
// Default Wake Words
// ============================================================================

const DEFAULT_WAKE_WORDS = [
  // Primary Vidai variants
  'hey vidai',
  'hi vidai', 
  'ok vidai',
  'okay vidai',
  'hello vidai',
  'vidai',
  'heyvidai',
  'hey vida',
  'hey vidi',
  'hey v die',
  'hey v day',
  'hey v dai',
  'a vidai',
  'hey v',
  // Hi Vidai variants
  'hi vida',
  'hi vidi',
  'hi v die',
  'hi v day',
  'hi v dai',
  'hividai',
  'hi video',
  'hi viday',
  'hi veda',
  'hi vitai',
  'hi viddy',
  'hi bidai',
  'hi be die',
  'hi we die',
  'hi we day',
  'hi vidya',
  'hi vidal',
  'hi vital',
  'hi vydai',
  'hi vedai',
  'hi widai',
  'hi bhidai',
  'hi beedai',
  // Hello Vidai variants
  'hello vida',
  'hello vidi',
  'hello v die',
  'hello v day',
  'hello v dai',
  'hellovidai',
  'hello v',
  'hello video',
  'hello viday',
  'hello veda',
  'hello vitai',
  'hello viddy',
  'hello bidai',
  'hello be die',
  'hello we die',
  'hello we day',
  'hello vidal',
  'hello vydai',
  'hello vedai',
  'hello widai',
  'hello bhidai',
  'hello beedai',
  // Common misrecognitions
  'hey vday',
  'hey vdai',
  'hey videi',
  'hey video',
  'hey viday',
  'hey veda',
  'hey vader',
  'hey vitai',
  'hey viddy',
  'hey bidai',
  'hey be die',
  'he vidai',
  'eva die',
  'eva day',
  'ava die',
  'hay vidai',
  'hey we die',
  'hey we day',
  // More Vidai misrecognitions
  'hey v.i.',
  'hey vi',
  'hey vii',
  'hey vide',
  'hey vidya',
  'hey vidya i',
  'hey vidal',
  'hey vital',
  'hey viday',
  'hey vydai',
  'hey vydei',
  'hey vide eye',
  'hey vide i',
  'hey vid eye',
  'hey vid i',
  'hey vid',
  'hey veed',
  'hey veeda',
  'hey veedi',
  'hey vedai',
  'hey vedei',
  'hey veedai',
  'hey via',
  'hey via die',
  'hey via day',
  'hey by die',
  'hey by day',
  'hey bye die',
  'hey bye day',
  'hey pie die',
  'hey pie day',
  'hey fi die',
  'hey fi day',
  'hey wifi',
  'hey wide eye',
  'hey wide i',
  'hey why die',
  'hey why day',
  // Indian accent variations
  'hey widai',
  'hey widei',
  'hey weedai',
  'hey bhidai',
  'hey bheedai',
  'hey beedai',
  // Without "hey" prefix
  'ok vida',
  'ok vidi',
  'okay vida',
  'okay vidi',
  // Phonetic variations
  'vi dai',
  'vee die',
  'vee day',
  'v die',
  'v day',
  'vida eye',
  'vida i',
  'vee dai',
  'vee dye',
  'vi die',
  'vi day',
  'vi dye',
  'vidaai',
  'vidaei',
  // Common assistant confusions
  'hey day',
  // Hey/Hi/Hello with spacing issues
  'heyvida',
  'heyvidai',
  'hivida',
  'hividai',
  'hellovida',
  'hellovidai',
  // More accent variations
  'hey we die',
  'hi we die',
  'hello we die',
  'hey bee die',
  'hi bee die',
  'hello bee die',
  'hey vee die',
  'hi vee die',
  'hello vee die',
  'hey wee die',
  'hi wee die',
  'hello wee die',
  // Generic wake words
  'listen',
  'are you listening',
  'can you hear me',
  'hear me',
  'do you hear me',
  'are you there',
  'you there',
  'hello there',
  'anybody there',
  'anyone there',
  'wake up',
  'start listening',
  // Navigation prefixes
  'navigate',
  'navigate to',
  'go to',
  'goto',
  'take me to',
  'take me',
  'bring me to',
  'show me',
  'switch to',
  'move to',
  'jump to',
  'head to',
  'redirect to',
  'redirect',
  'please open',
  'please show',
  'please go to',
  'can you open',
  'can you show',
  'can you go to',
];

// ============================================================================
// Wake Word Detection Hook
// ============================================================================

/**
 * Custom hook for wake word detection with optional direct command recognition.
 * 
 * Continuously listens for a configurable set of wake words (e.g., "Hey Vidai")
 * and triggers callbacks when detected. Can also detect navigation commands
 * directly without requiring a wake word (instant navigation mode).
 * 
 * @param options - Configuration options for wake word detection
 * @param options.wakeWords - Array of wake phrases to listen for (default: DEFAULT_WAKE_WORDS)
 * @param options.language - BCP 47 language code (default: 'en-US')
 * @param options.onWakeWordDetected - Callback fired when a wake word is detected
 * @param options.onCommandDetected - Callback fired when a high-confidence navigation command is detected
 * @param options.onTranscript - Callback fired with transcript updates
 * @param options.onError - Callback fired when an error occurs
 * @param options.enabled - Whether wake word detection is active (default: true)
 * @param options.detectCommands - If true, also detects navigation commands directly (default: false)
 * 
 * @returns Object containing detection status and control methods
 * 
 * @example
 * ```tsx
 * const { status, startListening, stopListening } = useWakeWord({
 *   enabled: true,
 *   detectCommands: true,
 *   onWakeWordDetected: (word) => console.log('Wake word:', word),
 *   onCommandDetected: (cmd, transcript) => navigate(cmd.route),
 * });
 * ```
 */
export function useWakeWord(options: UseWakeWordOptions = {}): UseWakeWordReturn {
  const {
    wakeWords = DEFAULT_WAKE_WORDS,
    language = 'en-US',
    onWakeWordDetected,
    onCommandDetected,
    onTranscript,
    onError,
    enabled = true,
    detectCommands = true, // Default to true for instant command detection
  } = options;

  const [status, setStatus] = useState<WakeWordStatus>('inactive');
  const [lastDetectedWord, setLastDetectedWord] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isListeningRef = useRef(false);
  const restartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldRestartRef = useRef(false);
  const isStartingRef = useRef(false); // Guard against multiple start attempts
  const abortCountRef = useRef(0); // Track consecutive aborts to prevent infinite loops
  const lastStartTimeRef = useRef(0); // Track when we last started
  const accumulatedTranscriptRef = useRef(''); // Accumulate transcript across events

  // Refs for callbacks
  const onWakeWordDetectedRef = useRef(onWakeWordDetected);
  const onCommandDetectedRef = useRef(onCommandDetected);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onWakeWordDetectedRef.current = onWakeWordDetected;
    onCommandDetectedRef.current = onCommandDetected;
    onTranscriptRef.current = onTranscript;
    onErrorRef.current = onError;
  }, [onWakeWordDetected, onCommandDetected, onTranscript, onError]);

  // Ref for detectCommands option
  const detectCommandsRef = useRef(detectCommands);
  useEffect(() => {
    detectCommandsRef.current = detectCommands;
  }, [detectCommands]);

  // Check browser support
  const isSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Check if transcript contains wake word
  const checkForWakeWord = useCallback((transcript: string): string | null => {
    const normalizedTranscript = transcript.toLowerCase().trim();
    
    for (const wakeWord of wakeWords) {
      if (normalizedTranscript.includes(wakeWord.toLowerCase())) {
        return wakeWord;
      }
    }
    return null;
  }, [wakeWords]);

  // Check if transcript matches a valid navigation command with high confidence
  const checkForCommand = useCallback((transcript: string): NormalizedCommand | null => {
    if (!detectCommandsRef.current) return null;
    
    const normalized = normalizeVoiceCommand(transcript);
    // Only return if confidence is very high (>=0.85) for instant navigation
    if (normalized && normalized.confidence >= 0.85) {
      return normalized;
    }
    return null;
  }, []);

  // Store checkForWakeWord in ref to avoid recreation
  const checkForWakeWordRef = useRef(checkForWakeWord);
  const checkForCommandRef = useRef(checkForCommand);
  useEffect(() => {
    checkForWakeWordRef.current = checkForWakeWord;
    checkForCommandRef.current = checkForCommand;
  }, [checkForWakeWord, checkForCommand]);

  // Ref for startListening to avoid circular dependency
  const startListeningRef = useRef<() => void>(() => {});

  // Ref for initializeRecognition to avoid circular dependency
  const initializeRecognitionRef = useRef<() => void>(() => {});

  // Start listening - stable version using refs
  const startListening = useCallback(() => {
    if (!isSupported) {
      setError('Speech recognition is not supported');
      setStatus('error');
      return;
    }

    // Prevent multiple simultaneous start attempts
    if (isListeningRef.current || isStartingRef.current) {
      console.log('[useWakeWord] Already listening or starting, skipping');
      return;
    }

    isStartingRef.current = true;
    shouldRestartRef.current = true;

    // Clean up any existing recognition instance
    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        // Ignore cleanup errors
      }
      recognitionRef.current = null;
    }

    // Request microphone permission first to avoid abort issues
    navigator.mediaDevices?.getUserMedia({ audio: true })
      .then((stream) => {
        // Got permission, stop the stream (recognition will request its own)
        stream.getTracks().forEach(track => track.stop());
        
        // Small delay to let the mic release
        setTimeout(() => {
          initializeRecognitionRef.current();
        }, 300);
      })
      .catch((err) => {
        console.log('[useWakeWord] Microphone permission denied:', err);
        setError('Microphone permission denied. Please allow microphone access.');
        setStatus('error');
        isStartingRef.current = false;
        shouldRestartRef.current = false;
      });
  }, [isSupported]);

  // Update ref after startListening is defined
  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  // Separate function to initialize recognition after permission check
  const initializeRecognition = useCallback(() => {
    if (!isSupported || !shouldRestartRef.current) {
      isStartingRef.current = false;
      return;
    }

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionClass();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = language;
    recognition.maxAlternatives = 3;

    recognition.onstart = () => {
      console.log('[useWakeWord] Recognition started - now listening for wake word');
      isListeningRef.current = true;
      isStartingRef.current = false;
      accumulatedTranscriptRef.current = ''; // Reset accumulated transcript
      setStatus('listening');
      setError(null);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      console.log('[useWakeWord] Got speech result:', event.results.length, 'results');
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        const isFinal = result.isFinal;

        console.log('[useWakeWord] Transcript:', transcript, 'isFinal:', isFinal);
        
        // Accumulate transcript to catch multi-word phrases
        if (isFinal) {
          accumulatedTranscriptRef.current += ' ' + transcript;
        } else {
          // For interim, use current transcript
          accumulatedTranscriptRef.current = transcript;
        }
        
        const fullTranscript = accumulatedTranscriptRef.current.trim();
        console.log('[useWakeWord] Full accumulated transcript:', fullTranscript);

        // Log all transcripts via callback
        onTranscriptRef.current?.(transcript, isFinal);

        // Check the accumulated transcript for wake word FIRST
        const detectedFromAccumulated = checkForWakeWordRef.current(fullTranscript);
        if (detectedFromAccumulated) {
          console.log('[useWakeWord] WAKE WORD DETECTED from accumulated:', detectedFromAccumulated);
          setLastDetectedWord(detectedFromAccumulated);
          setStatus('detected');
          
          recognition.stop();
          shouldRestartRef.current = false;
          accumulatedTranscriptRef.current = '';
          
          onWakeWordDetectedRef.current?.(detectedFromAccumulated);
          return;
        }

        // Check for valid navigation command (instant navigation without wake word)
        const matchedCommand = checkForCommandRef.current(fullTranscript);
        if (matchedCommand) {
          console.log('[useWakeWord] COMMAND DETECTED:', matchedCommand.label, 'confidence:', matchedCommand.confidence);
          setStatus('detected');
          
          recognition.stop();
          shouldRestartRef.current = false;
          accumulatedTranscriptRef.current = '';
          
          onCommandDetectedRef.current?.(matchedCommand, fullTranscript);
          return;
        }

        // Check all alternatives for wake word
        for (let j = 0; j < result.length; j++) {
          const alternative = result[j];
          const detectedWakeWord = checkForWakeWordRef.current(alternative.transcript);

          if (detectedWakeWord) {
            console.log('[useWakeWord] WAKE WORD DETECTED from alternative:', detectedWakeWord);
            setLastDetectedWord(detectedWakeWord);
            setStatus('detected');
            
            recognition.stop();
            shouldRestartRef.current = false;
            accumulatedTranscriptRef.current = '';
            
            onWakeWordDetectedRef.current?.(detectedWakeWord);
            return;
          }

          // Also check alternatives for commands
          const altCommand = checkForCommandRef.current(alternative.transcript);
          if (altCommand) {
            console.log('[useWakeWord] COMMAND DETECTED from alternative:', altCommand.label);
            setStatus('detected');
            
            recognition.stop();
            shouldRestartRef.current = false;
            accumulatedTranscriptRef.current = '';
            
            onCommandDetectedRef.current?.(altCommand, alternative.transcript);
            return;
          }
        }
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.log('[useWakeWord] Recognition error:', event.error);
      
      // Ignore 'no-speech' errors - just restart listening
      if (event.error === 'no-speech') {
        console.log('[useWakeWord] No speech detected, will auto-restart on end');
        isStartingRef.current = false;
        abortCountRef.current = 0; // Reset abort count on no-speech
        return;
      }
      
      // Handle 'aborted' errors with backoff
      if (event.error === 'aborted') {
        isStartingRef.current = false;
        
        // Track consecutive aborts
        const timeSinceLastStart = Date.now() - lastStartTimeRef.current;
        if (timeSinceLastStart < 1000) {
          abortCountRef.current++;
          console.log('[useWakeWord] Quick abort detected, count:', abortCountRef.current);
          
          // If too many quick aborts, stop trying for a while but auto-retry
          if (abortCountRef.current >= 5) {
            console.log('[useWakeWord] Too many aborts, pausing for 30 seconds');
            setStatus('error');
            setError('Microphone busy. Retrying in 30 seconds...');
            
            // Auto-retry after 30 seconds
            restartTimeoutRef.current = setTimeout(() => {
              abortCountRef.current = 0;
              setError(null);
              if (enabled) {
                startListeningRef.current();
              }
            }, 30000);
            return;
          }
        } else {
          // Reset count if abort wasn't quick
          abortCountRef.current = 0;
        }
        return;
      }

      setError(`Wake word error: ${event.error}`);
      setStatus('error');
      isListeningRef.current = false;
      isStartingRef.current = false;
      onErrorRef.current?.(`Wake word error: ${event.error}`);
    };

    recognition.onend = () => {
      console.log('[useWakeWord] Recognition ended, shouldRestart:', shouldRestartRef.current);
      isListeningRef.current = false;
      isStartingRef.current = false;

      // Auto-restart immediately if should continue listening
      // Only use delay if there were abort issues
      if (shouldRestartRef.current && enabled) {
        const delay = abortCountRef.current > 0 
          ? Math.min(500 * Math.pow(2, abortCountRef.current), 30000)
          : 100; // Quick restart - 100ms just to let browser release resources
        console.log('[useWakeWord] Will restart in', delay, 'ms');
        
        // Clear accumulated transcript for new session
        accumulatedTranscriptRef.current = '';
        
        restartTimeoutRef.current = setTimeout(() => {
          if (shouldRestartRef.current && !isListeningRef.current && !isStartingRef.current) {
            console.log('[useWakeWord] Auto-restarting recognition');
            startListeningRef.current();
          }
        }, delay);
      } else {
        setStatus('inactive');
      }
    };

    recognitionRef.current = recognition;

    try {
      lastStartTimeRef.current = Date.now();
      recognition.start();
      console.log('[useWakeWord] Started listening for wake word');
    } catch (err) {
      console.log('[useWakeWord] Start error (may be already started):', err);
      isStartingRef.current = false;
    }
  }, [isSupported, language, enabled]);

  // Update ref after initializeRecognition is defined
  useEffect(() => {
    initializeRecognitionRef.current = initializeRecognition;
  }, [initializeRecognition]);

  // Stop listening
  const stopListening = useCallback(() => {
    console.log('[useWakeWord] Stopping wake word listener');
    shouldRestartRef.current = false;
    isStartingRef.current = false;
    
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }

    if (recognitionRef.current) {
      try {
        // Use stop() instead of abort() for more graceful shutdown
        recognitionRef.current.stop();
      } catch {
        // Ignore errors during stop
      }
      recognitionRef.current = null;
    }

    isListeningRef.current = false;
    setStatus('inactive');
  }, []);

  // Store stopListening in ref for stable auto-start effect
  const stopListeningRef = useRef(stopListening);
  
  useEffect(() => {
    startListeningRef.current = startListening;
    stopListeningRef.current = stopListening;
  }, [startListening, stopListening]);

  // Watchdog timer - periodically check if recognition is still running
  useEffect(() => {
    if (!enabled || !isSupported) return;

    const watchdogInterval = setInterval(() => {
      // If we should be listening but we're not, restart
      if (shouldRestartRef.current && !isListeningRef.current && !isStartingRef.current) {
        console.log('[useWakeWord] Watchdog: Recognition not running, restarting...');
        startListeningRef.current();
      }
    }, 5000); // Check every 5 seconds

    return () => {
      clearInterval(watchdogInterval);
    };
  }, [enabled, isSupported]);

  // Auto-start when enabled - use refs to avoid dependency loops
  useEffect(() => {
    console.log('[useWakeWord] Auto-start effect - enabled:', enabled, 'isSupported:', isSupported);
    
    if (enabled && isSupported) {
      startListeningRef.current();
    } else {
      stopListeningRef.current();
    }

    return () => {
      stopListeningRef.current();
    };
  }, [enabled, isSupported]);

  return {
    status,
    isSupported,
    lastDetectedWord,
    error,
    startListening,
    stopListening,
  };
}

export default useWakeWord;
